import React, { CSSProperties, useCallback, useMemo, useRef } from "react";
import IconFloors from "../dynamicicons/icon-floors";
import IconPlace from "../dynamicicons/icon-place";
import Divider from "../elements/divider";
import VelavuMenu from "../elements/velavu-menu";
import { useToggleable } from "../helper/hook-helper";
import * as styles from "./floor-selector.module.scss";
import { VelavuFloor, VelavuSite } from "velavu-js-api";
import whitelabel from "../whitelabel";
import { classArr } from "../helper/style-helper";

interface FloorSelectorProps {
	site: VelavuSite;
	style?: CSSProperties;
	className?: string;
	selectedFloor?: VelavuFloor;
	onSelectFloor: (floorID: string | undefined) => void;
	onFloorClick?: VoidFunction;
}

export default function FloorSelector(props: FloorSelectorProps) {
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const [isFloorMenu, openFloorMenu, closeFloorMenu] = useToggleable();

	const selectFloor = useCallback(
		(floorID: string | undefined) => {
			closeFloorMenu();
			props.onSelectFloor(floorID);
		},
		[closeFloorMenu, props],
	);

	const displayFloors = useMemo(() => {
		if (props.site.floors === undefined) {
			return [];
		}

		//Sort in descending order
		return [...props.site.floors].sort((f1, f2) => f2.level - f1.level);
	}, [props.site]);

	return (
		<div className={`${styles.container} ${props.className ?? ""}`}>
			<div className={styles.content}>
				<div className={styles.site}>
					<IconPlace
						className={styles.pinIcon}
						color={whitelabel.palette.primary900}
						size={16}
					/>
					<span
						onClick={props.onFloorClick}
						className={classArr(
							props.onFloorClick && styles.clickableText,
						)}
					>
						{props.site.name}
					</span>
				</div>

				{displayFloors.length > 1 && (
					<>
						<Divider
							orientation="vertical"
							edgePadding
							style={{ marginLeft: 12, marginRight: 12 }}
						/>

						<button
							className={styles.floor}
							ref={buttonRef}
							onClick={openFloorMenu}
						>
							<IconFloors
								className={styles.floorIcon}
								color={whitelabel.palette.primary900}
							/>
							<span className={styles.floorLabel}>
								{props.selectedFloor?.name}
							</span>
						</button>

						<VelavuMenu
							className={styles.floorMenu}
							open={isFloorMenu}
							onClose={closeFloorMenu}
							refPosition={buttonRef.current}
							align="right"
							style={{ marginTop: 2 }}
						>
							{displayFloors.map((floor) => (
								<FloorMenuItem
									key={floor.id}
									label={floor.name}
									onClick={() => {
										selectFloor(floor.id);
									}}
								/>
							))}
						</VelavuMenu>
					</>
				)}
			</div>
		</div>
	);
}

function FloorMenuItem(props: { label: string; onClick?: VoidFunction }) {
	return (
		<button className={styles.menuItem} onClick={props.onClick}>
			<span className={styles.menuItemLabel}>{props.label}</span>
		</button>
	);
}
